import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { InputErrorMessage } from '@savvy-kit/components/input-error-message';
import InputField from '@savvy-kit/patterns/input-field';
import { FormikProvider, useFormik } from 'formik';
import { FormikSelectField } from 'src/components/formik-select-field';
import MonetaryInputTextField from 'src/components/mui/monetary-input-text-field';
import TrackedButton from 'src/components/tracked/tracked-button';
import {
  CREDIT_SUBTYPES,
  DEPOSITORY_SUB_TYPES_MANUAL_ADD,
  DEPRECATED_ACCOUNT_SUBTYPE_DISPLAY_NAMES,
  DEPRECATED_OTHER_ACCOUNT_DISPLAY_NAME,
  INVESTMENT_SUB_TYPES_MANUAL_ADD,
  isAccountTypeName,
  ItemType,
  LOAN_SUBTYPES,
} from 'src/constants/account-link';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { removeFalsey } from 'src/utils/object-manipulation';
import { mixed as yupMixed, number as yupNumber, object as yupObject, string as yupString } from 'yup';

import type { AccountSubtypeName, AccountTypeName, PortfolioItemTypeName } from '@savvy/constants/generated/gql-types';
import {
  AccountSubtypeName as AccountSubtypeNameEnum,
  AccountTypeName as AccountTypeNameEnum,
} from '@savvy/constants/generated/gql-types';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import defaultGuard from '@savvy/utils/default-guard';

export const FinancialAccountFormFragment = graphql(/* GraphQL */ `
  fragment FinancialAccountForm on Account {
    id
    name
    displayBalance {
      amount
    }
    type
    subtype
  }
`);

const MAX_STRING_LENGTH = 255;

const CREATE_ACCOUNT = graphql(/* GraphQL */ `
  mutation FinancialAccountFormCreate($input: CreateAccountInput!) {
    createAccount(input: $input) {
      account {
        id
      }
    }
  }
`);

const UPDATE_ACCOUNT = graphql(/* GraphQL */ `
  mutation FinancialAccountFormUpdate($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        displayBalance {
          amount
        }
      }
    }
  }
`);

function getPresetAccountType(itemType?: ItemType): AccountTypeName | undefined {
  switch (itemType) {
    case ItemType.BANK_ACCOUNT:
      return 'depository';
    case ItemType.INVESTMENT_ACCOUNT:
      return 'investment';
    case ItemType.COMPANY_EQUITY:
    case ItemType.OTHER_ACCOUNT:
    case ItemType.OTHER_ASSET:
    case ItemType.OTHER_LIABILITY:
    case ItemType.REAL_ESTATE:
    case undefined:
      return;
    default:
      defaultGuard(itemType);
      return;
  }
}

function getButtonName(itemType?: ItemType): string {
  const defaultString = 'Add account';
  switch (itemType) {
    case ItemType.BANK_ACCOUNT:
      return 'Add bank account';
    case ItemType.INVESTMENT_ACCOUNT:
      return 'Add investment account';
    case ItemType.COMPANY_EQUITY:
    case ItemType.OTHER_ACCOUNT:
    case ItemType.OTHER_ASSET:
    case ItemType.OTHER_LIABILITY:
    case ItemType.REAL_ESTATE:
    case undefined:
      return defaultString;
    default:
      return defaultGuard(itemType, defaultString);
  }
}

function getSubTypes(accountType: AccountTypeName): (AccountSubtypeName | 'other')[] {
  switch (accountType) {
    case 'depository':
      return DEPOSITORY_SUB_TYPES_MANUAL_ADD;
    case 'investment':
      return INVESTMENT_SUB_TYPES_MANUAL_ADD;
    case 'credit':
      return CREDIT_SUBTYPES;
    case 'loan':
      return LOAN_SUBTYPES;

    case 'other':
    case 'cryptocurrency':
    case 'employer_equity':
    case 'fund_investment':
    case 'insurance':
    case 'private_investment':
    default:
      return [];
  }
}

function subtypeDisplayText(
  type: AccountTypeName | PortfolioItemTypeName | undefined,
  subtype: AccountSubtypeName | 'other',
) {
  if (subtype === 'other' && type && isAccountTypeName(type) && type in DEPRECATED_OTHER_ACCOUNT_DISPLAY_NAME) {
    return DEPRECATED_OTHER_ACCOUNT_DISPLAY_NAME[type];
  }
  return DEPRECATED_ACCOUNT_SUBTYPE_DISPLAY_NAMES[subtype];
}

type FinancialAccountFormProps = {
  readonly accountFrag?: FragmentType<typeof FinancialAccountFormFragment>;
  readonly onSuccess: Deprecated_HandleItemUpdate;
  readonly itemType?: ItemType;
  readonly clientUserId?: string;
  readonly hiddenFromClient?: boolean;
  readonly errorMessage?: string;
};

export default function FinancialAccountForm({
  accountFrag,
  onSuccess,
  itemType,
  clientUserId,
  hiddenFromClient,
  errorMessage,
}: FinancialAccountFormProps) {
  const account = unmaskFragment(FinancialAccountFormFragment, accountFrag);
  const [createAccount] = useMutationWithErrorLogging(CREATE_ACCOUNT);
  const [updateAccount] = useMutationWithErrorLogging(UPDATE_ACCOUNT);
  const isSmallScreen = useIsSmallScreen();

  const handleCreateAccount = async () => {
    const res = await createAccount({
      variables: {
        input: {
          currentBalance: formatCurrencyForSubmit(formik.values.balance) ?? '',
          name: formik.values.name.trim(),
          subtype: formik.values.subtype ?? undefined,
          type: formik.values.type && isAccountTypeName(formik.values.type) ? formik.values.type : 'other',
          ...removeFalsey({
            currencyCode: 'USD',
            hiddenFromClient,
            userId: clientUserId,
          }),
        },
      },
    });
    void onSuccess({
      type: UpdateType.CREATE_ACCOUNT,
      id: res.data?.createAccount?.account.id ?? '',
    });
  };

  const handleUpdateAccount = async () => {
    await updateAccount({
      variables: {
        input: {
          currentBalance: formatCurrencyForSubmit(formik.values.balance),
          id: account?.id ?? '',
          name: formik.values.name.trim(),
          subtype: formik.values.subtype,
          type: formik.values.type && isAccountTypeName(formik.values.type) ? formik.values.type : 'other',
          ...removeFalsey({
            currencyCode: 'USD',
          }),
        },
      },
      refetchQueries: ['NetWorthPortfolio'],
    });
    void onSuccess({
      type: UpdateType.UPDATE_ACCOUNT,
      id: account?.id ?? '',
    });
  };

  const isEditMode = !!account;

  const presetAccountType = account?.type || getPresetAccountType(itemType);
  const formik = useFormik({
    initialValues: {
      name: account ? account.name : '',
      balance: account ? account.displayBalance.amount : '',
      type: account ? account.type : presetAccountType,
      subtype: account ? account.subtype : undefined,
      submit: null,
    },
    validationSchema: yupObject({
      name: yupString()
        .max(MAX_STRING_LENGTH, `Name must be at most ${MAX_STRING_LENGTH} characters`)
        .required('Name is required')
        .matches(/^[^/\\]*$/, 'Name cannot contain slashes or backslashes'),
      type: yupMixed<AccountTypeName>().required('Type is required').oneOf(Object.values(AccountTypeNameEnum)),
      subtype: yupMixed<AccountSubtypeName>().required('Type is required').oneOf(Object.values(AccountSubtypeNameEnum)),
      balance: yupNumber()
        .positive()
        .required(presetAccountType === 'investment' ? 'Value is required' : 'Account balance is required'),
    }),
    onSubmit: async (): Promise<void> => {
      if (isEditMode) {
        await handleUpdateAccount();
      } else {
        await handleCreateAccount();
      }
    },
  });

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit}>
        {errorMessage && (
          <div className="my-4">
            <InputErrorMessage message={errorMessage} />
          </div>
        )}
        <InputField
          id="name"
          containerClassName="mb-2"
          autoFocus={!isEditMode}
          error={Boolean(formik.touched.name && formik.errors.name && formik.dirty)}
          errorMessage={formik.touched.name && formik.dirty ? formik.errors.name : undefined}
          label="Name"
          name="name"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          type="text"
          value={formik.values.name}
        />
        {formik.values.type && isAccountTypeName(formik.values.type) && getSubTypes(formik.values.type).length > 0 && (
          <FormikSelectField
            name="subtype"
            label={presetAccountType ? 'Type' : 'Subtype'}
            className="mt-4"
            options={getSubTypes(formik.values.type).map((subtype) => ({
              value: subtype,
              label: subtypeDisplayText(formik.values.type, subtype) ?? subtype,
            }))}
          />
        )}
        <MonetaryInputTextField
          error={Boolean(formik.touched.balance && formik.errors.balance)}
          errorMessage={formik.errors.balance}
          label={presetAccountType === 'investment' ? 'Value' : 'Balance'}
          name="balance"
          onBlur={formik.handleBlur}
          formik={formik}
          value={formik.values.balance}
        />

        {formik.errors.submit && (
          <div className="mt-6">
            <InputErrorMessage message={formik.errors.submit} />
          </div>
        )}
        <div className="mb-6 mt-4 flex flex-row justify-end">
          <TrackedButton
            elementName={
              isEditMode ? 'financial-account-form-save-account-button' : 'financial-account-form-add-account-button'
            }
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
            className={isSmallScreen ? 'w-full' : undefined}
            type="submit"
          >
            {isEditMode ? 'Save account' : getButtonName(itemType)}
          </TrackedButton>
        </div>
      </form>
    </FormikProvider>
  );
}
